import React, { useEffect, useRef, useState } from 'react';
import { type TextInput, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Avatar, Button, Text, VStack } from '@squire/ui';

import { LockIcon, OpenLockIcon } from '@/components/_icons';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheetInput } from '@/components/_shared/BottomSheet';
import { ScreenHeader } from '@/components/_shared/ScreenHeader';
import { useValidateBarberPasscodeMutation } from '@/hooks/mutations/booking/useValidateBarberPasscodeMutation';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import composeRefs from '@/utils/composeRefs';
import { getShortName } from '@/utils/textUtils';

import { styles } from './styles';
import type { ProfessionalPasscodeBottomSheetProps } from './types';

export const ProfessionalPasscodeBottomSheet = React.forwardRef<
  BottomSheetRef,
  ProfessionalPasscodeBottomSheetProps
>(({ professional, isOpen, onSuccess, onCancel }, ref) => {
  const { t } = useScreenTranslations('ProfessionalPasscodeBottomSheet');
  const [passcode, setPasscode] = useState('');
  const [hasError, setHasError] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);

  const bottomSheetRef = useRef<BottomSheetRef>(null);
  const inputRef = useRef<TextInput>(null);
  const lockTransition = useSharedValue(0);

  const lockedStyle = useAnimatedStyle(() => ({
    opacity: 1 - lockTransition.value,
    transform: [{ scale: 1 - lockTransition.value * 0.3 }],
  }));

  const unlockedStyle = useAnimatedStyle(() => ({
    opacity: lockTransition.value,
    transform: [{ scale: 0.7 + lockTransition.value * 0.3 }],
  }));

  const { mutateAsync: validatePasscode, isPending: isLoading } =
    useValidateBarberPasscodeMutation();

  const shortName = getShortName(
    professional.barber.firstName,
    professional.barber.lastName,
  );

  const displayName = [
    professional.barber.firstName?.trim(),
    professional.barber.lastName?.trim(),
  ]
    .filter(Boolean)
    .join('\n');

  const clearState = () => {
    setPasscode('');
    setHasError(false);
    setIsUnlocked(false);
  };

  useEffect(() => {
    if (isOpen) {
      clearState();
      lockTransition.value = 0;
      bottomSheetRef.current?.open();
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen, lockTransition]);

  useEffect(() => {
    if (isUnlocked) {
      lockTransition.value = withTiming(1, { duration: 400 });
    }
  }, [isUnlocked, lockTransition]);

  const handleChangeText = (text: string) => {
    setPasscode(text);
    if (hasError) {
      setHasError(false);
    }
  };

  const handleSubmit = async () => {
    if (!passcode.trim()) {
      return;
    }

    try {
      const result = await validatePasscode({
        barberId: professional.barber.id,
        passcode: passcode.trim(),
      });

      if (result.passcode.valid) {
        setIsUnlocked(true);
        const trimmed = passcode.trim();
        setPasscode('');
        setTimeout(() => {
          onSuccess(trimmed);
        }, 1000);
      } else {
        setHasError(true);
      }
    } catch {
      setHasError(true);
    }
  };

  const handleClose = () => {
    bottomSheetRef.current?.close();
    clearState();
    onCancel();
  };

  return (
    <BottomSheet
      accessibilityLabel={t('title')}
      dismissible
      onDismiss={handleClose}
      hideHandle
      containerStyle={styles.bottomSheet}
      ref={composeRefs(ref, bottomSheetRef)}
    >
      <View style={styles.headerRow}>
        {professional.barber.photos?.[0]?.url && (
          <View style={styles.avatarWrapper}>
            <Avatar
              shape="square"
              size="lg"
              uri={professional.barber.photos[0].url}
              fallbackText={shortName}
            />
            {professional.barber.requirePasscode && (
              <View style={styles.lockIconWrapper}>
                <Animated.View style={[styles.lockedIcon, lockedStyle]}>
                  <LockIcon size={14} color="iconStatic" />
                </Animated.View>
                <Animated.View style={unlockedStyle}>
                  <OpenLockIcon size={14} color="iconStatic" />
                </Animated.View>
              </View>
            )}
          </View>
        )}
        <ScreenHeader
          title={displayName}
          onClose={handleClose}
          isLoading={isLoading}
          style={styles.headerContent}
        />
      </View>

      <VStack style={styles.inputContainer}>
        <Text
          variant="l"
          color={hasError ? 'textNegative' : 'textSecondary'}
          style={styles.inputLabel}
        >
          {hasError
            ? t('error.incorrectPassword')
            : isLoading
              ? t('verifying')
              : isUnlocked
                ? t('unlocked')
                : t('placeholder')}
        </Text>
        <BottomSheetInput
          ref={inputRef}
          value={passcode}
          onChangeText={handleChangeText}
          placeholder=""
          containerStyle={styles.input}
          style={styles.inputText}
          error={hasError}
          accessibilityLabel={t('accessibility.input')}
          accessibilityHint={t('accessibility.input')}
          maxLength={7}
          autoCapitalize="none"
          autoCorrect={false}
          secureTextEntry
          autoComplete="one-time-code"
        />
      </VStack>

      <Button
        onPress={handleSubmit}
        disabled={!passcode.trim() || isUnlocked || isLoading}
        loading={isLoading}
        accessibilityRole="button"
        accessibilityLabel={t('accessibility.submit')}
        variant="primary"
      >
        {isUnlocked ? t('unlocked') : t('submit')}
      </Button>
    </BottomSheet>
  );
});

ProfessionalPasscodeBottomSheet.displayName = 'ProfessionalPasscodeBottomSheet';
